
import { useState } from "react";
import { HexColorPicker } from "react-colorful";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface ColorPickerProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
  themeColors?: Record<string, string>;
}

export const ColorPicker = ({ value, onChange, label, themeColors }: ColorPickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const [activeTab, setActiveTab] = useState(value.includes('gradient') ? 'gradient' : 'solid');

  const handleSolidColorChange = (color: string) => {
    setLocalValue(color);
    onChange(color);
  };

  const handleGradientChange = (gradientValue: string) => {
    setLocalValue(gradientValue);
    onChange(gradientValue);
  };

  return (
    <div className="space-y-2">
      {label && <Label>{label}</Label>}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="w-10 h-10 p-0 rounded-full"
            style={{
              background: localValue || '#000000',
              backgroundImage: localValue.includes('gradient') ? localValue : 'none'
            }}
          />
        </PopoverTrigger>
        <PopoverContent className="w-80" align="end">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full mb-4">
              <TabsTrigger value="solid" className="flex-1">Solid</TabsTrigger>
              <TabsTrigger value="gradient" className="flex-1">Gradient</TabsTrigger>
            </TabsList>
            <TabsContent value="solid" className="space-y-4">
              <HexColorPicker
                color={localValue}
                onChange={handleSolidColorChange}
              />
              {themeColors && (
                <div className="mt-4">
                  <Label className="mb-2 block">Theme Colors</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {Object.entries(themeColors).map(([key, color]) => (
                      <Button
                        key={key}
                        variant="outline"
                        size="sm"
                        onClick={() => handleSolidColorChange(color)}
                        className="w-8 h-8 p-0 rounded-full"
                        style={{ 
                          backgroundColor: color,
                          borderColor: localValue === color ? themeColors.primary : 'transparent',
                          borderWidth: localValue === color ? '2px' : '1px'
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>
            <TabsContent value="gradient" className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                {[
                  'linear-gradient(to right, #00b4db, #0083b0)',
                  'linear-gradient(to right, #ff416c, #ff4b2b)',
                  'linear-gradient(to right, #834d9b, #d04ed6)',
                  'linear-gradient(to right, #4facfe, #00f2fe)',
                  'linear-gradient(to right, #43e97b, #38f9d7)',
                  'linear-gradient(to right, #fa709a, #fee140)',
                ].map((gradient, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    className="h-8 w-full"
                    style={{ background: gradient }}
                    onClick={() => handleGradientChange(gradient)}
                  />
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </PopoverContent>
      </Popover>
    </div>
  );
};
